import { type Listing, type ListingStatus } from '@carsfinders/contracts';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';

import { t } from '../../lib/i18n';
import {
  allowedTransitions,
  bulkTargets,
  canQuickMove,
  entryStatus,
  isAllSelected,
  isTransitionSubmittable,
  MIN_REJECT_REASON_LENGTH,
  normalizeRejectReason,
  toggleSelectAll,
  toggleSelection,
  type QueueEntry,
  type QueueStatusFilter,
} from './moderation-queue';

/** Statuses that actually have a public marketplace page (mirrors the API). */
const PUBLICLY_VISIBLE: readonly ListingStatus[] = ['active', 'reserved', 'sold'];

/** Currency prefixes — the artboard prints «€21 900», symbol first. */
const CURRENCY_PREFIX: Record<string, string> = {
  EUR: '€',
  USD: '$',
  UAH: '₴',
  CHF: 'CHF ',
};

/** Design D chip colours per listing status — shared with the «Мої оголошення» tab. */
export const STATUS_CHIP: Record<ListingStatus, string> = {
  draft: 'bg-cf-fill text-cf-ink-subtle',
  moderation: 'bg-cf-warn-tint text-cf-warn',
  active: 'bg-cf-success-tint text-cf-success',
  reserved: 'bg-cf-info-tint text-cf-info-ink',
  sold: 'bg-cf-fill text-cf-ink-muted',
  rejected: 'bg-cf-danger-tint text-cf-danger',
  archived: 'bg-cf-fill text-cf-ink-subtle',
};

export interface ModerationQueueTabProps {
  queue: QueueEntry[];
  selected: string[];
  /** The status the feed is currently filtered to. */
  statusFilter: QueueStatusFilter;
  /** Every selectable status (from the shared contract). */
  statuses: readonly ListingStatus[];
  onStatusFilterChange: (next: QueueStatusFilter) => void;
  isLoading: boolean;
  isError: boolean;
  onRefresh: () => void;
  onSelectedChange: (next: string[]) => void;
  onAdd: (listingId: string, note: string) => void;
  onRemove: (listingId: string) => void;
  onApprove: (listingIds: string[]) => void;
  onReject: (listingIds: string[], reason: string) => void;
  onTransition: (
    listingId: string,
    to: ListingStatus,
    options: { adminOverride?: boolean; reason?: string },
  ) => void;
  isBusy: boolean;
}

/**
 * Moderation queue — `Redesign-D-Admin`, screen «Маркетплейс».
 *
 * The artboard makes the photo the row: a listing is approved or bounced on
 * what the car looks like and what the seller wrote, so every entry is a card
 * with the first photo at 3:2, the price in mono, and the two verdicts under
 * it. A text row could not carry that decision.
 *
 * Two controls on this screen are NOT on the artboard and are kept because
 * dropping them would remove capability the operator already has: the bulk
 * select/approve/reject strip, and the per-card «змінити статус / прибрати»
 * footer. They are drawn in the same language rather than left in the old
 * blue-and-grey.
 */
export function ModerationQueueTab({
  queue,
  selected,
  statusFilter,
  statuses,
  onStatusFilterChange,
  isLoading,
  isError,
  onRefresh,
  onSelectedChange,
  onAdd,
  onRemove,
  onApprove,
  onReject,
  onTransition,
  isBusy,
}: ModerationQueueTabProps) {
  const [newId, setNewId] = useState('');
  const [newNote, setNewNote] = useState('');
  /** Ids the pending reject modal will act on ([] = closed). */
  const [rejectTargets, setRejectTargets] = useState<string[]>([]);
  const [rejectReason, setRejectReason] = useState('');
  const [transitionTarget, setTransitionTarget] = useState<QueueEntry | null>(null);

  const allSelected = isAllSelected(queue, selected);
  // Bulk actions only count the selected rows that can actually move there.
  const approvable = bulkTargets(queue, selected, 'active');
  const rejectable = bulkTargets(queue, selected, 'rejected');
  const reasonOk = normalizeRejectReason(rejectReason) !== null;

  const closeReject = () => {
    setRejectTargets([]);
    setRejectReason('');
  };

  return (
    <>
      {/* ── Filter row: which status, plus pinning one id outside it ────── */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          id="queue-status-filter"
          aria-label={t('marketplace.admin.queue.statusFilter')}
          value={statusFilter}
          onChange={(event) => onStatusFilterChange(event.target.value as QueueStatusFilter)}
          className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-sm"
          data-testid="queue-status-filter"
        >
          <option value="all">{t('marketplace.admin.queue.statusAll')}</option>
          {statuses.map((status) => (
            <option key={status} value={status}>
              {t(`marketplace.admin.queue.status.${status}`)}
            </option>
          ))}
        </select>

        <form
          className="flex h-10 max-w-[420px] min-w-0 flex-1 basis-[260px] items-center gap-1.5 rounded-cf-md border border-cf-line bg-cf-surface pr-1.5 pl-3.5"
          onSubmit={(event) => {
            event.preventDefault();
            onAdd(newId, newNote);
            setNewId('');
            setNewNote('');
          }}
        >
          <input
            id="queue-add-id"
            value={newId}
            onChange={(event) => setNewId(event.target.value)}
            placeholder={t('marketplace.admin.queue.addPlaceholder')}
            className="min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
          <button
            type="submit"
            disabled={newId.trim().length === 0}
            className="h-[30px] rounded-cf-sm bg-cf-fill px-3 text-[13px] font-semibold disabled:opacity-50"
          >
            {t('marketplace.admin.queue.add')}
          </button>
        </form>

        {/* The moderator's memo for a pinned id — it becomes that row's subtitle. */}
        <input
          id="queue-add-note"
          value={newNote}
          onChange={(event) => setNewNote(event.target.value)}
          placeholder={t('marketplace.admin.queue.addNote')}
          aria-label={t('marketplace.admin.queue.note')}
          className="h-10 max-w-[320px] min-w-0 flex-1 basis-[200px] rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-sm"
        />

        <button
          type="button"
          onClick={onRefresh}
          disabled={isLoading}
          data-testid="queue-refresh"
          className="inline-flex h-10 items-center gap-2 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-sm font-semibold hover:border-cf-ink disabled:opacity-50"
        >
          <RefreshCw size={16} strokeWidth={1.75} className={isLoading ? 'animate-spin' : ''} />
          {t('marketplace.admin.queue.refresh')}
        </button>
      </div>

      {isError && (
        <p
          className="text-[13px] text-cf-danger"
          role="alert"
          data-testid="queue-load-error"
        >
          {t('marketplace.admin.queue.loadError')}
        </p>
      )}

      {isLoading && queue.length === 0 ? (
        <Notice text={t('marketplace.admin.queue.loading')} testId="queue-loading" />
      ) : queue.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* ── Bulk strip (not on the artboard; see the file comment) ──── */}
          <div className="flex flex-wrap items-center gap-2.5 rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-2.5">
            <label className="inline-flex items-center gap-2 text-[13px] text-cf-ink-muted">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => onSelectedChange(toggleSelectAll(queue, selected))}
                data-testid="queue-select-all"
                className="accent-cf-brand"
              />
              {t('marketplace.admin.queue.selectAll')}
            </label>
            <span className="font-mono text-[11px] text-cf-ink-subtle">
              {t('marketplace.admin.queue.selectedCount', { count: String(selected.length) })}
            </span>
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={() => onApprove(approvable)}
                disabled={approvable.length === 0 || isBusy}
                data-testid="queue-bulk-approve"
                className="h-9 rounded-cf-md bg-cf-success px-3 text-[13px] font-semibold text-white disabled:opacity-50"
              >
                {t('marketplace.admin.queue.bulkApprove')}
              </button>
              <button
                type="button"
                onClick={() => setRejectTargets(rejectable)}
                disabled={rejectable.length === 0 || isBusy}
                data-testid="queue-bulk-reject"
                className="h-9 rounded-cf-md border border-[rgba(220,38,38,0.35)] bg-cf-surface px-3 text-[13px] font-semibold text-cf-danger disabled:opacity-50"
              >
                {t('marketplace.admin.queue.bulkReject')}
              </button>
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-[repeat(auto-fill,minmax(300px,1fr))]">
            {queue.map((entry) => (
              <ListingCard
                key={entry.listingId}
                entry={entry}
                checked={selected.includes(entry.listingId)}
                onToggle={() => onSelectedChange(toggleSelection(selected, entry.listingId))}
                onApprove={() => onApprove([entry.listingId])}
                onReject={() => setRejectTargets([entry.listingId])}
                onTransition={() => setTransitionTarget(entry)}
                onRemove={() => onRemove(entry.listingId)}
                isBusy={isBusy}
              />
            ))}
          </div>
        </>
      )}

      {rejectTargets.length > 0 && (
        <Modal titleKey="marketplace.admin.queue.rejectModal.title" onClose={closeReject}>
          <p className="text-[13px] leading-[1.5] text-cf-ink-subtle">
            {t('marketplace.admin.queue.rejectModal.body')}
          </p>
          <textarea
            value={rejectReason}
            onChange={(event) => setRejectReason(event.target.value)}
            rows={3}
            placeholder={t('marketplace.admin.queue.rejectModal.placeholder')}
            className="mt-3 w-full resize-y rounded-cf-md border border-cf-line px-3 py-2.5 text-[13px]"
            data-testid="queue-reject-reason"
          />
          <p className="mt-1 text-right font-mono text-[11px] text-cf-ink-subtle">
            {rejectReason.trim().length}/{MIN_REJECT_REASON_LENGTH}
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={closeReject}
              className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-sm font-semibold hover:border-cf-ink"
            >
              {t('marketplace.admin.queue.rejectModal.cancel')}
            </button>
            <button
              type="button"
              disabled={!reasonOk || isBusy}
              onClick={() => {
                const reason = normalizeRejectReason(rejectReason);
                if (!reason) return;
                onReject(rejectTargets, reason);
                closeReject();
              }}
              data-testid="queue-reject-confirm"
              className="h-10 rounded-cf-md bg-cf-danger px-3.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {t('marketplace.admin.queue.rejectModal.confirm')}
            </button>
          </div>
        </Modal>
      )}

      {transitionTarget && (
        <TransitionModal
          entry={transitionTarget}
          onClose={() => setTransitionTarget(null)}
          onApply={(to, options) => {
            onTransition(transitionTarget.listingId, to, options);
            setTransitionTarget(null);
          }}
          isBusy={isBusy}
        />
      )}
    </>
  );
}

/** «2018 · 133 527 km · Львів» — the artboard's second line. */
function listingMeta(listing: Listing): string {
  const parts = [
    String(listing.year),
    `${new Intl.NumberFormat('uk-UA').format(listing.mileage)} km`,
  ];
  if (listing.location) parts.push(listing.location);
  return parts.join(' · ');
}

/** «€21 900» — symbol first, thin space groups, no decimals. */
function listingPrice(listing: Listing): string {
  const prefix = CURRENCY_PREFIX[listing.currency] ?? `${listing.currency} `;
  return `${prefix}${new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 0 }).format(listing.price)}`;
}

/**
 * The artboard's note line («Фото 12 · VIN збігається з виграним лотом 3E19»)
 * is a moderator's own observation. What the record carries instead is the
 * photo count plus whatever honesty text is attached — a moderation reason on a
 * bounced listing, a damage disclosure otherwise. The reason is tinted, because
 * it is the one that changes the decision.
 */
function listingNote(listing: Listing): { text: string; tone: string } {
  const photos = t('marketplace.admin.queue.photoCount', {
    count: String(listing.photos?.length ?? 0),
  });
  if (listing.moderationReason) {
    return { text: `${photos} · ${listing.moderationReason}`, tone: 'text-cf-warn' };
  }
  if (listing.damageDisclosure) {
    return { text: `${photos} · ${listing.damageDisclosure}`, tone: 'text-cf-ink-subtle' };
  }
  return { text: photos, tone: 'text-cf-ink-subtle' };
}

/** One queued listing, drawn as the artboard's photo card. */
function ListingCard({
  entry,
  checked,
  onToggle,
  onApprove,
  onReject,
  onTransition,
  onRemove,
  isBusy,
}: {
  entry: QueueEntry;
  checked: boolean;
  onToggle: () => void;
  onApprove: () => void;
  onReject: () => void;
  onTransition: () => void;
  onRemove: () => void;
  isBusy: boolean;
}) {
  // The admin feed carries the whole listing, including the ones in moderation
  // that the public detail endpoint refuses to serve — no per-row fetch needed.
  const listing = entry.listing ?? null;
  const status = entryStatus(entry);
  const canApprove = canQuickMove(status, 'active');
  const canReject = canQuickMove(status, 'rejected');
  const unavailable =
    status === undefined
      ? undefined
      : t('marketplace.admin.queue.actionUnavailable', {
          status: t(`marketplace.admin.queue.status.${status}`),
        });
  const photo = listing?.photos?.[0];
  const note = listing ? listingNote(listing) : null;

  return (
    <article
      className="flex flex-col overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface"
      data-testid={`queue-row-${entry.listingId}`}
    >
      <div className="relative aspect-3/2 bg-cf-fill">
        {photo ? (
          <img src={photo} alt="" className="block h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center font-mono text-[10px] tracking-[0.08em] text-cf-ink-subtle uppercase">
            {t('marketplace.admin.queue.noPhoto')}
          </span>
        )}
        <span className="absolute top-2.5 left-2.5 max-w-[60%] truncate rounded-cf-sm bg-cf-surface/95 px-2 py-[5px] font-mono text-[10px] tracking-[0.08em] text-cf-ink-muted uppercase">
          {entry.listingId}
        </span>
        <label className="absolute top-2.5 right-2.5 flex h-7 w-7 items-center justify-center rounded-cf-sm bg-cf-surface/95">
          <input
            type="checkbox"
            checked={checked}
            onChange={onToggle}
            aria-label={t('marketplace.admin.queue.selectOne')}
            data-testid={`queue-select-${entry.listingId}`}
            className="accent-cf-brand"
          />
        </label>
      </div>

      <div className="flex flex-1 flex-col gap-1.5 px-4 py-3.5">
        <h3 className="text-base font-semibold tracking-[-0.01em]">
          {listing?.title ?? entry.listingId}
        </h3>
        <p className="text-xs text-cf-ink-subtle">
          {listing ? listingMeta(listing) : entry.note || t('marketplace.admin.queue.pendingDetail')}
        </p>

        <div className="mt-1 flex items-baseline justify-between gap-2">
          <span className="font-mono text-[20px] leading-none font-medium">
            {listing ? listingPrice(listing) : '—'}
          </span>
          <span className="text-xs text-cf-ink-subtle">
            {listing
              ? t(`marketplace.admin.queue.seller.${listing.listingMode}`)
              : t('marketplace.admin.queue.seller.pinned')}
          </span>
        </div>

        {note && <p className={`mt-1 text-xs leading-[1.4] ${note.tone}`}>{note.text}</p>}

        {status && (
          <span
            className={`mt-1 inline-flex w-fit rounded-cf-sm px-2 py-1 font-mono text-[10px] tracking-[0.08em] uppercase ${STATUS_CHIP[status]}`}
          >
            {t(`marketplace.admin.queue.status.${status}`)}
          </span>
        )}
      </div>

      <div className="flex gap-1.5 px-4 pb-3.5">
        <button
          type="button"
          onClick={onApprove}
          disabled={isBusy || !canApprove}
          title={canApprove ? undefined : unavailable}
          data-testid={`queue-approve-${entry.listingId}`}
          className="h-9 flex-1 rounded-cf-md bg-cf-success text-[13px] font-semibold text-white disabled:opacity-50"
        >
          {t('marketplace.admin.queue.approve')}
        </button>
        <button
          type="button"
          onClick={onReject}
          disabled={isBusy || !canReject}
          title={canReject ? undefined : unavailable}
          data-testid={`queue-reject-${entry.listingId}`}
          className="h-9 flex-1 rounded-cf-md border border-cf-line bg-cf-surface text-[13px] font-semibold hover:border-cf-ink disabled:opacity-50"
        >
          {t('marketplace.admin.queue.reject')}
        </button>
      </div>

      {/* Capability the artboard does not draw — kept, in the same language. */}
      <div className="flex items-center gap-3 border-t border-cf-line-soft px-4 py-2 text-xs">
        <button
          type="button"
          onClick={onTransition}
          disabled={isBusy}
          className="font-semibold text-cf-brand disabled:opacity-50"
        >
          {t('marketplace.admin.queue.transition')}
        </button>
        {status !== undefined && PUBLICLY_VISIBLE.includes(status) && (
          <a
            href={`/marketplace/${encodeURIComponent(entry.listingId)}`}
            target="_blank"
            rel="noreferrer"
            className="text-cf-ink-subtle hover:text-cf-brand"
          >
            {t('marketplace.admin.queue.openPublic')}
          </a>
        )}
        <button
          type="button"
          onClick={onRemove}
          data-testid={`queue-remove-${entry.listingId}`}
          className="ml-auto text-cf-ink-subtle hover:text-cf-danger"
        >
          {t('marketplace.admin.queue.remove')}
        </button>
      </div>
    </article>
  );
}

/** Generic status-change modal, gated by the shared transition guard. */
function TransitionModal({
  entry,
  onClose,
  onApply,
  isBusy,
}: {
  entry: QueueEntry;
  onClose: () => void;
  onApply: (to: ListingStatus, options: { adminOverride?: boolean; reason?: string }) => void;
  isBusy: boolean;
}) {
  const from: ListingStatus = entry.lastKnownStatus ?? 'moderation';
  const [adminOverride, setAdminOverride] = useState(false);
  const [reason, setReason] = useState('');
  const options = allowedTransitions(from, adminOverride);
  const [to, setTo] = useState<ListingStatus | ''>(options[0] ?? '');

  const submittable = to !== '' && isTransitionSubmittable(from, to, { adminOverride, reason });

  return (
    <Modal titleKey="marketplace.admin.queue.transitionModal.title" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <div>
          <label
            htmlFor="transition-to"
            className="mb-1 block font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase"
          >
            {t('marketplace.admin.queue.transitionModal.target')}
          </label>
          <select
            id="transition-to"
            value={to}
            onChange={(event) => setTo(event.target.value as ListingStatus)}
            className="h-10 w-full rounded-cf-md border border-cf-line bg-cf-surface px-3 text-sm"
            data-testid="transition-target"
          >
            {options.map((status) => (
              <option key={status} value={status}>
                {t(`marketplace.admin.queue.status.${status}`)}
              </option>
            ))}
          </select>
        </div>

        <label className="inline-flex items-center gap-2 text-[13px] text-cf-ink-muted">
          <input
            type="checkbox"
            checked={adminOverride}
            onChange={(event) => {
              setAdminOverride(event.target.checked);
              setTo('');
            }}
            data-testid="transition-override"
            className="accent-cf-brand"
          />
          {t('marketplace.admin.queue.transitionModal.override')}
        </label>

        <div>
          <label
            htmlFor="transition-reason"
            className="mb-1 block font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase"
          >
            {t('marketplace.admin.queue.transitionModal.reason')}
          </label>
          <input
            id="transition-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="h-10 w-full rounded-cf-md border border-cf-line bg-cf-surface px-3 text-sm"
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-sm font-semibold hover:border-cf-ink"
        >
          {t('marketplace.admin.queue.transitionModal.cancel')}
        </button>
        <button
          type="button"
          disabled={!submittable || isBusy}
          onClick={() =>
            to !== '' && onApply(to, { adminOverride, reason: reason.trim() || undefined })
          }
          data-testid="transition-confirm"
          className="h-10 rounded-cf-md bg-cf-brand px-3.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          {t('marketplace.admin.queue.transitionModal.confirm')}
        </button>
      </div>
    </Modal>
  );
}

/**
 * «У цьому статусі оголошень немає», over the illustration the artboard pairs
 * it with (90px on the phone, 110px on the desktop).
 */
function EmptyState() {
  return (
    <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-7 text-center lg:px-6 lg:py-10">
      <img
        src="/assets/cf/illu/lots.png"
        alt=""
        className="mx-auto mb-2.5 block h-[90px] w-auto lg:mb-3 lg:h-[110px]"
      />
      <p className="text-base font-semibold lg:text-[18px]" data-testid="queue-empty">
        {t('marketplace.admin.queue.empty')}
      </p>
    </section>
  );
}

function Notice({ text, testId }: { text: string; testId?: string }) {
  return (
    <section
      className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-7 text-center text-[13px] text-cf-ink-subtle lg:px-6 lg:py-10"
      data-testid={testId}
    >
      {text}
    </section>
  );
}

/** Minimal centred modal shell (the admin SPA has no dialog primitive yet). */
function Modal({
  titleKey,
  onClose,
  children,
}: {
  titleKey: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-cf-ink/50 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-cf-lg border border-cf-line bg-cf-surface p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="text-[17px] font-semibold tracking-[-0.02em]">{t(titleKey)}</h3>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}
